import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Target, Shield, TrendingUp, Award, CheckCircle } from 'lucide-react';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/g2ajmgxi_Ville-Haute_vue_du_Fort_Verlorenkost_%28cropped%29.jpg';
const TEAM_IMAGE = 'https://images.unsplash.com/photo-1573166364839-1bfe9196c23e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZyUyMGRpdmVyc2UlMjBleGVjdXRpdmV8ZW58MHx8fHwxNzcwODk0MTEzfDA&ixlib=rb-4.1.0&q=85';

const HomePage = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: Target,
      title: t('services.recruitment.title'),
      description: t('services.recruitment.outcomes'),
      link: '/services#recruitment'
    },
    {
      icon: Users,
      title: t('services.teamDynamics.title'),
      description: t('services.teamDynamics.outcomes'),
      link: '/services#team-dynamics'
    },
    {
      icon: TrendingUp,
      title: t('services.leadership.title'),
      description: t('services.leadership.outcomes'),
      link: '/services#leadership'
    }
  ];

  const stats = [
    { value: '15+', label: 'Years Experience' },
    { value: '200+', label: 'Teams Transformed' },
    { value: '95%', label: 'Client Satisfaction' },
    { value: 'LU', label: 'Luxembourg Based' }
  ];

  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background with parallax - Luxembourg landscape */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundAttachment: 'fixed',
            backgroundPosition: 'center 40%'
          }}
        />
        {/* Premium dark gradient overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.70) 50%, rgba(0,0,0,0.80) 100%)'
          }}
        />
        
        <div className="container-custom py-24 relative z-10">
          <div className="max-w-3xl">
            <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-6 animate-fade-in-up">
              {t('hero.tagline')}
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6 animate-fade-in-up stagger-1">
              {t('hero.headline')}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-10 animate-fade-in-up stagger-2">
              {t('hero.subheadline')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <Link to="/diagnosis">
                <Button className="btn-bronze text-lg px-10 py-6 flex items-center gap-2" data-testid="hero-cta-btn">
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1A1A1A] text-lg px-10 py-6" data-testid="hero-secondary-btn">
                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 bg-white" data-testid="value-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-4">
                {t('about.vision')}
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold text-[#1A1A1A] leading-tight mb-6">
                {t('about.visionText')}
              </h2>
              <p 
                className="text-lg text-gray-600 leading-relaxed mb-8 tracking-wide"
                dangerouslySetInnerHTML={{ __html: t('about.missionText') }}
              />
              <div className="flex flex-col gap-4">
                {['Sustainable performance', 'Leadership alignment', 'Culture reinforcement'].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#C5A059]" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src={TEAM_IMAGE} 
                alt="Team collaboration"
                className="rounded-sm shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#1A1A1A] text-white p-6 max-w-xs">
                <p className="text-3xl font-bold text-[#C5A059] mb-2">15+</p>
                <p className="text-sm text-gray-300">Years of transforming teams in Luxembourg</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview - Bento Grid */}
      <section className="py-24 section-alt" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-[#C5A059] uppercase tracking-widest text-sm font-medium mb-4">
              {t('services.title')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A1A1A]">
              {t('services.subtitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <Link 
                key={idx} 
                to={service.link}
                className="group bg-white p-8 border border-gray-200 hover:border-[#C5A059]/50 transition-all duration-300 card-hover"
                data-testid={`service-card-${idx}`}
              >
                <div className="w-14 h-14 bg-[#1A1A1A] flex items-center justify-center mb-6 group-hover:bg-[#C5A059] transition-colors">
                  <service.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-[#1A1A1A] mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                <span className="flex items-center gap-2 text-[#1A1A1A] font-medium group-hover:text-[#C5A059] transition-colors">
                  {t('common.learnMore')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#1A1A1A]" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-5xl md:text-6xl font-bold text-[#C5A059] mb-2 font-mono">{stat.value}</p>
                <p className="text-gray-400 text-sm uppercase tracking-wide">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white grid-pattern" data-testid="cta-section">
        <div className="container-custom text-center">
          <div className="max-w-2xl mx-auto">
            <Shield className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A1A1A] mb-6">
              Ready to Transform Your Team?
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              Take our free Team Performance Diagnosis and discover actionable insights to improve your team's execution.
            </p>
            <Link to="/diagnosis">
              <Button className="btn-primary text-lg px-12 py-6 flex items-center gap-2 mx-auto" data-testid="final-cta-btn">
                {t('hero.cta')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 border-t border-gray-100" data-testid="certifications-section">
        <div className="container-custom">
          <p className="text-center text-gray-500 text-sm uppercase tracking-widest mb-8">
            {t('about.certifications')}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8">
            {[
              { name: 'LeSS', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/c68od8uh_Less%20certified.png' },
              { name: 'SAFe', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/zycy1zj3_Safe%20Certified.png' },
              { name: 'TKI', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/6ygieiqm_TKI.png' },
              { name: 'PCM', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/0y90nuj4_PCM.png' },
              { name: 'MBTI', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/gi19s4uw_MBTI.png' },
              { name: 'NLP', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/5khbpxnk_NLP.jpg' },
              { name: 'Gallup', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/w9z58jx6_Gallup.png' },
              { name: 'Prosci', logo: 'https://customer-assets.emergentagent.com/job_teamcraft-app-2/artifacts/l1jzaol8_prosci.png' }
            ].map((cert, idx) => (
              <div key={idx} className="h-20 w-28 bg-white rounded-md p-3 flex items-center justify-center shadow-sm" title={cert.name}>
                <img 
                  src={cert.logo} 
                  alt={cert.name}
                  className="max-h-14 max-w-24 object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
